import { queuesForVariation } from '@axle-health/work-queue-definitions';
import { Ordering } from 'gql-types';

import {
    defaultEpisodeOfCareRowToLink,
    defaultEpisodeVisitsRowToLink,
    defaultVisitsRowToLink,
} from '../definitions';
import { definitionFor, fromDefinition } from '../fromDefinition';
import {
    EpisodeOfCareDashboardContainer,
    PatientDashboardContainer,
    VisitDashboardContainer,
} from '../types';
import { HH_CONTAINER_KEYS } from './containerKeys/containerKeys';
import { IDashboardVariationConfig } from './types';

/**
 * Home Health dashboard config, modeled on the Penn Medicine at Home WorkQueues spec.
 */

const PUBLISHED_QUEUES = queuesForVariation('HOME_HEALTH_DEMO');

const patientSchedulingLink = ({ id }: { id: string }): string => `/patients/${id}/scheduling`;

const INCOMPLETE_CARE_TEAM_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.incompleteCareTeam),
    {
        defaultEnabled: true,
        hoverText: 'Patients that need care teams assigned.',
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No patients with incomplete care teams.',
            // TODO(BE-304): Spec wants "sort by number of days since SOC completion
            // date / episode start date, oldest first". That requires sorting patients
            // by a property of a filtered subset of their visits.
            ordering: { firstName: Ordering.ASC },
        },
    },
);

const SOC_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.soc),
    {
        defaultEnabled: true,
        hoverText: 'Start of Care visits that need scheduling.',
        tableConfig: {
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No SOC visits to schedule.',
            ordering: { fulfillmentWindowEndDate: Ordering.ASC },
        },
    },
);

const ROC_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.roc),
    {
        defaultEnabled: true,
        hoverText: 'Resumption of Care visits that need scheduling.',
        tableConfig: {
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No ROC visits to schedule.',
            ordering: { fulfillmentWindowEndDate: Ordering.ASC },
        },
    },
);

const EVAL_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.eval),
    {
        defaultEnabled: true,
        hoverText: 'Eval visits that need scheduling.',
        tableConfig: {
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No Eval visits to schedule.',
            ordering: { fulfillmentWindowEndDate: Ordering.ASC },
        },
    },
);

const EXPIRING_FULFILLMENT_WINDOW_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.expiringFulfillmentWindow),
    {
        defaultEnabled: true,
        hoverText: 'Visits with expiring fulfillment windows.',
        tableConfig: {
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No visits with expiring fulfillment windows.',
            ordering: { fulfillmentWindowEndDate: Ordering.ASC },
        },
    },
);

const PATIENTS_NEEDING_SCHEDULING_CONTAINER: EpisodeOfCareDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.patientsNeedingScheduling),
    {
        defaultEnabled: true,
        hoverText: 'Patients that need scheduling.',
        tableConfig: {
            rowToLink: defaultEpisodeOfCareRowToLink,
            emptyTableText: 'No patients to schedule.',
            // TODO: spec wants fulfillment window end date, soonest first, which is
            // min(visit.fulfillmentWindowEndDate) across the episode.
            ordering: { startDate: Ordering.ASC },
        },
    },
);

const OVERDUE_VISITS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.overdueVisits),
    {
        defaultEnabled: true,
        hoverText: 'Upcoming visits more than 15 mins. overdue.',
        extraProps: { showClinicianColumn: true },
        tableConfig: {
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No visits overdue by more than 15 mins.',
            ordering: { startDatetime: Ordering.ASC },
        },
    },
);

const PATIENT_NO_SHOWS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.patientNoShows),
    {
        defaultEnabled: true,
        hoverText: 'Patient no-shows requiring attention.',
        tableConfig: {
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No patient no-shows requiring attention.',
            ordering: { startDatetime: Ordering.DESC },
        },
    },
);

const PENDING_END_OF_EPISODE_CONTAINER: EpisodeOfCareDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.pendingEndOfEpisode),
    {
        defaultEnabled: true,
        hoverText: 'Episodes ending soon with no recert or discharge scheduled.',
        tableConfig: {
            rowToLink: defaultEpisodeOfCareRowToLink,
            emptyTableText: 'No episodes pending end of care.',
            ordering: { endDate: Ordering.ASC },
        },
    },
);

const TRANSFERS_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.transfers),
    {
        defaultEnabled: true,
        hoverText: 'Patients on hold with scheduled future visits.',
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No transfers requiring attention.',
            // TODO: spec wants the earliest date of upcoming visits.
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const DISCHARGE_WITH_PENDING_VISITS_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.dischargeWithPendingVisits),
    {
        defaultEnabled: true,
        hoverText: 'Discharged patients with pending future visits.',
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No discharges with pending visits.',
            // TODO: same derived-field issue as Transfers.
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const INACTIVE_CLINICIAN_VISITS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.inactiveClinicianVisits),
    {
        defaultEnabled: true,
        hoverText: 'Visits assigned to inactive clinicians.',
        tableConfig: {
            rowToLink: defaultEpisodeVisitsRowToLink,
            emptyTableText: 'No visits assigned to inactive clinicians.',
            ordering: { effectiveDate: Ordering.ASC },
        },
    },
);

const NO_FUTURE_APPOINTMENTS_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.noFutureAppointments),
    {
        defaultEnabled: true,
        hoverText: 'Active patients with no future appointments scheduled.',
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No active patients without future appointments.',
            // TODO: spec wants days since last visit, longest first.
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const COMPLETED_VISITS_NOT_MAPPED_TO_ORDERS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.completedVisitsNotMappedToOrders),
    {
        defaultEnabled: true,
        hoverText: 'Completed SOC/ROC/Eval visits with no linked order.',
        tableConfig: {
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No completed SOC/ROC/Eval visits unlinked to orders.',
            ordering: { startDatetime: Ordering.ASC },
        },
    },
);

const EVALS_BEFORE_SOCS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.evalsBeforeSocs),
    {
        defaultEnabled: true,
        hoverText: 'Evals scheduled or completed before the patient’s SOC.',
        tableConfig: {
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No Evals scheduled before SOCs.',
            ordering: { startDatetime: Ordering.ASC },
        },
    },
);

const MULTIPLE_OASIS_PER_EPISODE_CONTAINER: EpisodeOfCareDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.multipleOasisPerEpisode),
    {
        defaultEnabled: true,
        hoverText: 'Episodes with more than one OASIS Discharge or Recert visit pending.',
        tableConfig: {
            rowToLink: defaultEpisodeOfCareRowToLink,
            emptyTableText: 'No episodes with multiple OASIS Discharge or Recert visits.',
            ordering: { startDate: Ordering.ASC },
        },
    },
);

const PENDING_REASSESSMENT_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HH_CONTAINER_KEYS.pendingReassessment),
    {
        defaultEnabled: true,
        hoverText: 'Patients due for reassessment with none scheduled.',
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No patients pending reassessment.',
            // TODO: spec wants days since the previous reassessment visit, highest
            // first.
            ordering: { fullName: Ordering.ASC },
        },
    },
);

export const HOME_HEALTH_DASHBOARD_CONFIG: IDashboardVariationConfig = {
    containers: [
        INCOMPLETE_CARE_TEAM_CONTAINER,
        SOC_CONTAINER,
        ROC_CONTAINER,
        EVAL_CONTAINER,
        EXPIRING_FULFILLMENT_WINDOW_CONTAINER,
        PATIENTS_NEEDING_SCHEDULING_CONTAINER,
        OVERDUE_VISITS_CONTAINER,
        PATIENT_NO_SHOWS_CONTAINER,
        PENDING_END_OF_EPISODE_CONTAINER,
        TRANSFERS_CONTAINER,
        DISCHARGE_WITH_PENDING_VISITS_CONTAINER,
        INACTIVE_CLINICIAN_VISITS_CONTAINER,
        NO_FUTURE_APPOINTMENTS_CONTAINER,
        COMPLETED_VISITS_NOT_MAPPED_TO_ORDERS_CONTAINER,
        EVALS_BEFORE_SOCS_CONTAINER,
        MULTIPLE_OASIS_PER_EPISODE_CONTAINER,
        PENDING_REASSESSMENT_CONTAINER,
    ],
};
